import { useCallback, useEffect, useState } from "react";
import { adminCostCalibration } from "@/lib/api";
import { toast } from "sonner";
import { RefreshCw, AlertTriangle, Sparkles, FileWarning } from "lucide-react";

const CONF_TONE = {
    high: "bl-badge bl-badge-low",
    medium: "bl-badge bl-badge-medium",
    low: "bl-badge bl-badge-high",
};

const fmtMoney = (n) =>
    n == null ? "—" :
    new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 })
        .format(Number(n));

/**
 * Bad-estimates dashboard powered by the per-issue cost_calibration_log.
 * Default view: width_capped=true OR confidence=low — the rows that most
 * commonly need a scope_key tightened or a baseline adjusted.
 */
export default function CostCalibrationAdmin() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [count, setCount] = useState(0);
    const [filter, setFilter] = useState("bad"); // bad | low | capped | all

    const params = useCallback(() => {
        if (filter === "low") return { confidence: "low", limit: 100 };
        if (filter === "capped") return { width_capped: true, limit: 100 };
        if (filter === "bad") return { limit: 100 }; // we'll filter client-side
        return { limit: 100 };
    }, [filter]);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const r = await adminCostCalibration(params());
            let data = r.rows || [];
            if (filter === "bad") {
                data = data.filter(
                    (row) => row.width_capped || row.confidence === "low",
                );
            }
            setRows(data);
            setCount(r.count || data.length);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load calibration log");
        } finally {
            setLoading(false);
        }
    }, [filter, params]);

    useEffect(() => {
        load();
    }, [load]);

    const filters = [
        { id: "bad",    label: "Bad estimates (recommended)", icon: AlertTriangle },
        { id: "low",    label: "Low confidence",              icon: FileWarning },
        { id: "capped", label: "Width-capped only",            icon: Sparkles },
        { id: "all",    label: "All recent",                  icon: null },
    ];

    return (
        <div data-testid="cost-calibration-admin">
            <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
                <div className="flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-bl-primary" />
                    <h2 className="font-display font-semibold text-xl text-bl">
                        Repair Exposure Calibration
                    </h2>
                    <span className="text-bl-muted text-sm">
                        ({count} row{count === 1 ? "" : "s"})
                    </span>
                </div>
                <button
                    onClick={load}
                    disabled={loading}
                    className="bl-btn bl-btn-ghost text-xs flex items-center gap-1.5"
                    data-testid="calibration-refresh"
                >
                    <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
                    Refresh
                </button>
            </div>

            <p className="text-bl-muted text-sm mb-4 max-w-3xl">
                Per-issue AI vs cost-engine outputs from recent reports. Watch this list
                weekly to find scopes that consistently width-cap or hit low confidence —
                strong signals that a new `scope_key` or a tighter baseline could help.
            </p>

            <div className="flex flex-wrap gap-2 mb-4">
                {filters.map((f) => {
                    const Icon = f.icon;
                    return (
                        <button
                            key={f.id}
                            onClick={() => setFilter(f.id)}
                            className={`bl-badge ${filter === f.id ? "bl-badge-medium" : "bl-badge-neutral"} flex items-center gap-1 cursor-pointer`}
                            data-testid={`calibration-filter-${f.id}`}
                        >
                            {Icon && <Icon className="w-3 h-3" />} {f.label}
                        </button>
                    );
                })}
            </div>

            <div className="bl-card overflow-x-auto" data-testid="calibration-table">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="text-left">
                            {[
                                "Created", "Category", "Issue", "Scope key", "ZIP",
                                "Range", "Confidence", "Width capped", "Why",
                            ].map((h) => (
                                <th
                                    key={h}
                                    className="px-3 py-3 text-[10px] tracking-[0.2em] uppercase text-bl-muted font-medium border-b border-bl whitespace-nowrap"
                                >
                                    {h}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 && (
                            <tr>
                                <td colSpan={9} className="px-4 py-12 text-center text-bl-muted">
                                    No flagged rows. Generate a few reports to populate the log.
                                </td>
                            </tr>
                        )}
                        {rows.map((r, i) => (
                            <tr key={i} className="border-b border-bl last:border-0 align-top">
                                <td className="px-3 py-3 text-xs text-bl-muted font-mono-bl whitespace-nowrap">
                                    {r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}
                                </td>
                                <td className="px-3 py-3 text-bl text-xs">{r.category || "—"}</td>
                                <td className="px-3 py-3 text-bl text-xs max-w-[200px]">
                                    {r.title || "—"}
                                    <div className="font-mono-bl text-[10px] text-bl-muted mt-0.5 break-all">{r.report_id}</div>
                                </td>
                                <td className="px-3 py-3 font-mono-bl text-[11px] text-bl">{r.scope_key || "fallback"}</td>
                                <td className="px-3 py-3 font-mono-bl text-[11px] text-bl">
                                    {r.zip_code_used || <span className="text-bl-muted">—</span>}
                                </td>
                                <td className="px-3 py-3 font-mono-bl text-xs text-bl whitespace-nowrap">
                                    {fmtMoney(r.low)} – {fmtMoney(r.high)}
                                </td>
                                <td className="px-3 py-3">
                                    <span className={CONF_TONE[r.confidence] || "bl-badge bl-badge-neutral"}>
                                        {r.confidence || "—"}
                                    </span>
                                </td>
                                <td className="px-3 py-3">
                                    {r.width_capped ? (
                                        <span className="bl-badge bl-badge-medium text-[10px]">Capped</span>
                                    ) : <span className="text-bl-muted">—</span>}
                                </td>
                                <td className="px-3 py-3 text-bl-muted text-xs max-w-[280px] leading-relaxed">
                                    {r.confidence_reason || "—"}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
